import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import ClaimArtistFlow from "../components/ClaimArtistFlow";
import { PROS } from "../lib/statuses";
import api, { API } from "../lib/api";
import { toast } from "sonner";

export default function Settings() {
  const { user, profile, loading, updateProfile, refresh, refreshBilling, logout } = useAuth();
  const sub = useSubscription();
  const nav = useNavigate();

  const [form, setForm] = useState({
    artist_name: "", stage_name: "", bio: "",
    default_pro: "", default_publisher: "", default_label: "", default_distributor: "",
    social_handles: { instagram: "", tiktok: "", twitter: "", youtube: "", spotify_url: "", apple_url: "" },
  });
  const [connections, setConnections] = useState([]);
  const [showClaim, setShowClaim] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/auth"); }, [loading, user, nav]);

  useEffect(() => {
    if (profile) {
      setForm({
        artist_name: profile.artist_name || "",
        stage_name: profile.stage_name || "",
        bio: profile.bio || "",
        default_pro: profile.default_pro || "",
        default_publisher: profile.default_publisher || "",
        default_label: profile.default_label || "",
        default_distributor: profile.default_distributor || "",
        social_handles: {
          instagram: profile.social_handles?.instagram || "",
          tiktok: profile.social_handles?.tiktok || "",
          twitter: profile.social_handles?.twitter || "",
          youtube: profile.social_handles?.youtube || "",
          spotify_url: profile.social_handles?.spotify_url || "",
          apple_url: profile.social_handles?.apple_url || "",
        },
      });
    }
  }, [profile]);

  const loadConnections = useCallback(async () => {
    try {
      const r = await api.get("/connections");
      setConnections(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user) loadConnections(); }, [user, loadConnections]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_handles: { ...f.social_handles, [k]: v } }));

  const onSave = async () => {
    setBusy(true);
    try {
      await updateProfile(form);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1800);
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onResync = async (conn) => {
    if (!sub.canBulkImport) {
      window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: "bulk_import" } }));
      return;
    }
    try {
      await api.post(`/connections/${conn.id}/resync`);
      toast.success(`Re-syncing ${conn.platform_artist_name}…`);
      setTimeout(loadConnections, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-sync failed");
    }
  };

  const onDisconnect = async (conn) => {
    if (!window.confirm("Disconnect Apple Music? Imported tracks stay.")) return;
    try {
      await api.delete(`/connections/${conn.id}`);
      setConnections((prev) => prev.filter((c) => c.id !== conn.id));
    } catch {
      toast.error("Disconnect failed");
    }
  };

  const exportJson = async () => {
    const token = localStorage.getItem("lq_token");
    const r = await fetch(`${API}/export/json`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "lacquer-export.json"; a.click();
    URL.revokeObjectURL(url);
  };
  const exportCsv = async () => {
    const token = localStorage.getItem("lq_token");
    const r = await fetch(`${API}/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "lacquer-export.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    try {
      const data = JSON.parse(text);
      const trks = data.tracks || data;
      const mode = window.confirm("Click OK to MERGE with existing tracks (deduped by ISRC), Cancel to REPLACE entire catalog.")
        ? "merge" : "replace";
      const r = await api.post("/import", { tracks: trks, mode });
      toast.success(`Imported: ${r.data.inserted} new, ${r.data.updated} updated, ${r.data.skipped} skipped`);
    } catch (err) {
      toast.error("Invalid JSON file");
    }
  };

  const onUpgrade = async (plan) => {
    try {
      const r = await api.post("/billing/checkout-session", { plan, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    }
  };

  const onPortal = async () => {
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot open portal");
    }
  };

  const onChangePassword = async (e) => {
    e?.preventDefault?.();
    setPwError("");
    setPwSuccess(false);
    if (!currentPassword) { setPwError("Current password required"); return; }
    if (newPassword.length < 8) { setPwError("New password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setPwError("New password and confirmation do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/profile/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwSuccess(true);
      toast.success("Password updated");
      setTimeout(() => setPwSuccess(false), 3500);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.error === "current_password_incorrect") {
        setPwError("Current password is incorrect");
      } else {
        setPwError(typeof det === "string" ? det : "Password update failed");
      }
    } finally {
      setPwBusy(false);
    }
  };

  const onDeleteAccount = async () => {
    if (!window.confirm("Delete your account and all catalog data? Cannot be undone. Type DELETE to confirm next prompt.")) return;
    const a = window.prompt("Type DELETE to confirm:");
    if (a !== "DELETE") return;
    try {
      await api.delete("/auth/me");
      logout();
    } catch {
      toast.error("Failed");
    }
  };

  const appleConn = connections.find((c) => c.platform === "apple");
  const usagePct = sub.tier === "free" ? Math.min(100, (sub.trackCount / sub.trackLimit) * 100) : 0;
  const subBadgeClass =
    sub.status === "trialing" ? "trial" :
    sub.status === "active" ? "active" :
    sub.status === "past_due" ? "past_due" :
    sub.status === "canceled" ? "canceled" : "free";

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="settings-page">
        <button className="btn btn-ghost" onClick={() => nav("/dashboard")} style={{ paddingLeft: 0, marginBottom: 6 }} data-testid="back-to-library">← Back to Library</button>
        <div className="eyebrow">— Profile</div>
        <h1 className="serif" style={{ fontSize: 56, lineHeight: 0.95, margin: "10px 0 28px", letterSpacing: "-0.02em" }}>
          Settings
        </h1>

        {/* Identity */}
        <Section icon="i" title="Identity">
          <div className="settings-grid-2">
            <Field label="Artist Name"><input className="lq-input" value={form.artist_name} onChange={(e) => set("artist_name", e.target.value)} data-testid="settings-artist-name" /></Field>
            <Field label="Stage Name"><input className="lq-input" value={form.stage_name} onChange={(e) => set("stage_name", e.target.value)} /></Field>
          </div>
          <Field label="Bio"><textarea className="lq-textarea" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} /></Field>
        </Section>

        {/* Social Handles */}
        <Section icon="@" title="Social Handles">
          <div className="settings-grid-2">
            <Field label="Instagram"><input className="lq-input" value={form.social_handles.instagram} onChange={(e) => setSocial("instagram", e.target.value)} placeholder="@handle" /></Field>
            <Field label="TikTok"><input className="lq-input" value={form.social_handles.tiktok} onChange={(e) => setSocial("tiktok", e.target.value)} placeholder="@handle" /></Field>
            <Field label="Twitter / X"><input className="lq-input" value={form.social_handles.twitter} onChange={(e) => setSocial("twitter", e.target.value)} placeholder="@handle" /></Field>
            <Field label="YouTube"><input className="lq-input" value={form.social_handles.youtube} onChange={(e) => setSocial("youtube", e.target.value)} placeholder="@channel" /></Field>
            <Field label="Spotify Artist URL"><input className="lq-input" type="url" value={form.social_handles.spotify_url} onChange={(e) => setSocial("spotify_url", e.target.value)} /></Field>
            <Field label="Apple Music Artist URL"><input className="lq-input" type="url" value={form.social_handles.apple_url} onChange={(e) => setSocial("apple_url", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Connected Services */}
        <Section icon="⌁" title="Connected Services">
          <p style={{ color: "var(--text-mute)", fontSize: 13, marginBottom: 14 }}>
            Streaming profiles you've claimed. Re-syncing pulls any new tracks since the last import. Disconnecting keeps imported tracks but stops future syncs.
          </p>

          {[
            { key: "apple", icon: "♪", name: "Apple Music", conn: appleConn },
          ].map(({ key, icon, name, conn }) => (
            <div key={key} className="connection-row">
              <div className={`connection-icon ${conn ? key : "empty"}`}>{icon}</div>
              <div className="connection-info">
                <div className="connection-name">
                  {conn ? `${name} · ${conn.platform_artist_name}` : name}
                </div>
                <div className="connection-detail">
                  {conn
                    ? <><span style={{ color: "var(--green-light)" }}>●</span> Connected · last synced {conn.last_synced_at ? new Date(conn.last_synced_at).toLocaleString() : "never"}</>
                    : "Not connected"}
                </div>
              </div>
              <div className="connection-actions">
                {conn ? (
                  <>
                    <button className="connection-btn" onClick={() => onResync(conn)} data-testid={`resync-${key}`}>Re-sync</button>
                    <button className="connection-btn danger" onClick={() => onDisconnect(conn)} data-testid={`disconnect-${key}`}>Disconnect</button>
                  </>
                ) : (
                  <button
                    className="connection-btn primary"
                    onClick={() => {
                      if (!sub.canBulkImport) {
                        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: "bulk_import" } }));
                        return;
                      }
                      setShowClaim(true);
                    }}
                    data-testid={`connect-${key}`}
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
          ))}
        </Section>

        {/* Defaults */}
        <Section icon="⊕" title="Track Defaults">
          <p style={{ color: "var(--text-mute)", fontSize: 13, marginBottom: 14 }}>
            Auto-applied to every new track. Each can be overridden per-track.
          </p>
          <div className="settings-grid-2">
            <Field label="Default PRO">
              <select className="lq-select" value={form.default_pro} onChange={(e) => set("default_pro", e.target.value)}>
                {PROS.map((p) => <option key={p} value={p}>{p || "—"}</option>)}
              </select>
            </Field>
            <Field label="Default Publisher"><input className="lq-input" value={form.default_publisher} onChange={(e) => set("default_publisher", e.target.value)} /></Field>
            <Field label="Default Label"><input className="lq-input" value={form.default_label} onChange={(e) => set("default_label", e.target.value)} /></Field>
            <Field label="Default Distributor"><input className="lq-input" value={form.default_distributor} onChange={(e) => set("default_distributor", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Subscription */}
        <Section icon="$" title="Subscription">
          <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <span className={`sub-badge ${subBadgeClass}`} data-testid="sub-badge">
              {sub.status === "trialing" ? "Trial"
                : sub.status === "active" ? "Active"
                : sub.status === "past_due" ? "Past Due"
                : sub.status === "canceled" ? "Canceled"
                : "Free"}
            </span>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 14, color: "var(--text)" }}>
                {sub.tier === "pro" && sub.status === "active" && `Pro · $${sub.plan === "annual" ? "72/yr" : "8/mo"}`}
                {sub.status === "trialing" && "Pro · 14-day free trial"}
                {sub.tier === "free" && "Free · 25 track limit"}
                {sub.status === "past_due" && "Payment past due"}
                {sub.status === "canceled" && "Canceled"}
              </div>
              <div className="data-line" style={{ marginTop: 2 }}>
                {sub.status === "trialing" && (sub.trialDaysRemaining ?? 0) > 0 && (
                  <>{sub.trialDaysRemaining} day{sub.trialDaysRemaining === 1 ? "" : "s"} remaining
                  {sub.trialEndsAt && ` · Renews ${new Date(sub.trialEndsAt).toLocaleDateString()}`}
                  {!sub.hasCard && " · No card on file"}</>
                )}
                {sub.status === "active" && sub.periodEnd && `Next billing: ${new Date(sub.periodEnd).toLocaleDateString()}`}
              </div>
            </div>
            {(sub.status === "trialing" || sub.tier === "free") && (
              <button className="btn btn-primary" onClick={() => onUpgrade("monthly")} data-testid="add-payment-btn">
                {sub.tier === "free" ? "Upgrade to Pro" : "Add Payment"}
              </button>
            )}
          </div>

          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 13.5, color: "var(--text)" }}>Track usage</div>
            <div className="data-line">
              {sub.trackCount} {sub.tier === "pro" ? "tracks · Unlimited" : `tracks · ${sub.trackLimit} limit on Free`}
            </div>
            {sub.tier === "free" && (
              <div className="usage-bar">
                <div className={`usage-fill ${usagePct >= 100 ? "full" : ""}`} style={{ width: `${usagePct}%` }} />
              </div>
            )}
          </div>

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border-soft)", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {sub.tier === "pro" && (
              <button className="connection-btn" onClick={onPortal} data-testid="manage-billing-btn">Manage Billing</button>
            )}
            <Link to="/pricing" className="connection-btn" data-testid="view-plans-btn">View Plans</Link>
            {sub.tier === "pro" && sub.status === "active" && (
              <button className="connection-btn danger" onClick={onPortal}>Cancel Subscription</button>
            )}
          </div>
        </Section>

        {/* Account */}
        <Section icon="⌘" title="Account">
          <Field label="Email"><input className="lq-input" type="email" value={user?.email || ""} disabled /></Field>

          <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--border-soft)" }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>— Change Password</div>
            <form onSubmit={onChangePassword} data-testid="change-password-form">
              <Field label="Current Password">
                <input
                  type="password"
                  className="lq-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid="current-password-input"
                  autoComplete="current-password"
                />
              </Field>
              <div className="settings-grid-2">
                <Field label="New Password">
                  <input
                    type="password"
                    className="lq-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    data-testid="new-password-input"
                    autoComplete="new-password"
                  />
                  {newPassword && newPassword.length < 8 && (
                    <div className="data-line" style={{ color: "var(--red-light)", marginTop: 4 }}>
                      Too short — needs 8+ characters
                    </div>
                  )}
                  {newPassword && newPassword.length >= 8 && (
                    <div className="data-line" style={{ color: "var(--green-light)", marginTop: 4 }}>
                      {newPassword.length >= 12 ? "Strong" : "OK · 12+ chars recommended"}
                    </div>
                  )}
                </Field>
                <Field label="Confirm New Password">
                  <input
                    type="password"
                    className="lq-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password-input"
                    autoComplete="new-password"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <div className="data-line" style={{ color: "var(--red-light)", marginTop: 4 }}>
                      Doesn&apos;t match
                    </div>
                  )}
                </Field>
              </div>
              {pwError && (
                <div className="data-line" style={{ color: "var(--red-light)", marginTop: 8 }} data-testid="pw-error">
                  ✕ {pwError}
                </div>
              )}
              {pwSuccess && (
                <div className="data-line" style={{ color: "var(--green-light)", marginTop: 8 }} data-testid="pw-success">
                  ✓ Password updated.
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={pwBusy}
                  data-testid="update-password-btn"
                >
                  {pwBusy ? "Updating…" : "Update Password"}
                </button>
              </div>
            </form>
          </div>

          <div style={{ marginTop: 16, padding: 14, border: "1px solid rgba(159, 18, 57, 0.30)", borderRadius: 4 }}>
            <div className="mono" style={{ color: "var(--red-light)", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 6 }}>Danger Zone</div>
            <p style={{ color: "var(--text-mute)", fontSize: 13, marginBottom: 12 }}>Permanently delete this account and all associated data. Cannot be undone — export a backup first.</p>
            <button className="btn btn-danger" onClick={onDeleteAccount} data-testid="delete-account-btn">Delete Account</button>
          </div>
        </Section>

        {/* Data */}
        <Section icon="↓" title="Data">
          <p style={{ color: "var(--text-mute)", fontSize: 13, marginBottom: 12 }}>
            Export your full catalog or restore from a backup. Your data is yours — always.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="connection-btn" onClick={exportJson} data-testid="export-json-btn">Export JSON ↓</button>
            <button className="connection-btn" onClick={exportCsv} data-testid="export-csv-btn">Export CSV ↓</button>
            <label className="connection-btn" style={{ cursor: "pointer" }}>
              Import JSON ↑
              <input type="file" accept="application/json" style={{ display: "none" }} onChange={onImportFile} data-testid="import-json-input" />
            </label>
          </div>
        </Section>

        {/* Save bar */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center", marginTop: 20 }}>
          <span className={`saved-toast ${savedToast ? "show" : ""}`}>✓ Saved</span>
          <button className="btn btn-ghost" onClick={() => nav("/dashboard")}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={busy} data-testid="save-settings-btn">
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>

        {/* Admin-only sections */}
        {user?.is_admin && (
          <>
            <div style={{ marginTop: 56, marginBottom: 18 }}>
              <div className="eyebrow" style={{ color: "var(--accent)" }}>— Admin Tools</div>
              <h2 className="serif" style={{ fontSize: 32, lineHeight: 1.05, margin: "8px 0 0", letterSpacing: "-0.01em" }}>
                Operations
              </h2>
            </div>

            <Section icon="◊" title="Admin · User Management">
              <p style={{ color: "var(--text-mute)", fontSize: 13.5, marginBottom: 14 }}>
                Manage users, grant or revoke Pro access, reset passwords, and review the audit log.
              </p>
              <button className="connection-btn primary" onClick={() => nav("/admin")} data-testid="open-admin-users-btn">
                Open Admin Console →
              </button>
            </Section>

            <Section icon="◇" title="Admin · Resources">
              <p style={{ color: "var(--text-mute)", fontSize: 13.5, marginBottom: 14 }}>
                Curate the external resources directory visible to all users.
              </p>
              <button className="connection-btn primary" onClick={() => nav("/admin/resources")} data-testid="open-resource-manager-btn">
                Open Resource Manager →
              </button>
            </Section>
          </>
        )}
      </div>

      {/* Claim modal */}
      {showClaim && (
        <div className="modal-backdrop" onClick={() => setShowClaim(false)}>
          <div className="modal-card" style={{ maxWidth: 560, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <ClaimArtistFlow
              defaultName={form.artist_name}
              onClose={() => setShowClaim(false)}
              onDone={() => {
                setShowClaim(false);
                loadConnections();
                refreshBilling();
                refresh();
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="settings-section">
      <div className="settings-section-head">
        <div className="settings-section-icon">{icon}</div>
        <div className="settings-section-title">{title}</div>
      </div>
      <div className="settings-section-body">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ marginBottom: 12 }}><label className="label">{label}</label>{children}</div>;
}
